/**
 * Foundational onboarding tour for BuildIt CRM (v2).
 *
 * NARROWED to a 3-stop, single-page tour anchored entirely on `/contacts`.
 *
 * Why narrowed (vs. the v1 7-stop tour merged in PR #328):
 *   v1 spanned the contacts list, the create-contact modal, the contact-detail
 *   page sidebar, and the user-nav dropdown. react-joyride does not orchestrate
 *   route changes or modal open/close — the moment a step's anchor isn't on
 *   the current page, it fires `error:target_not_found`, which our
 *   TourProvider treats as a clean tour-end. In real usage that orphaned
 *   the tour after the first stop. Adding route/modal coordination logic
 *   to TourProvider is non-trivial; we deferred it.
 *
 * v2 strategy: keep the foundational tour scoped to one page, where every
 * anchor is guaranteed to render. Per-feature tours (create-contact modal
 * walkthrough, contact-detail sidebar walkthrough, etc.) can be triggered
 * contextually when the user first reaches each surface — that's a future
 * piece of work, not part of the foundational tour.
 *
 * The data-tour-id anchors that v1 referenced but v2 no longer uses
 * (`create-contact-modal.status-field`, `contact-detail.click-to-dial`,
 * `contact-detail.notes`, `contact-detail.interests`, `contact-detail.groups`)
 * stay in place in their components. They are harmless when unused and will
 * be picked up by the deferred contextual per-feature tours.
 *
 * Version bump rationale:
 *   The version is bumped from 'v1' → 'v2'. Users who completed or
 *   permanently dismissed v1 still have a `user_onboarding` row keyed by
 *   `(user_id, tour_id='foundational', version='v1')`. The gating query in
 *   `useFoundationalTour` matches on (tour_id, version), so bumping to 'v2'
 *   means those users will be re-prompted with the new (shorter) tour —
 *   which is the intended behavior for a re-scoped foundational onboarding.
 *
 * tour_id:  'foundational'
 * version:  'v2'
 *
 * Final step renders the "Don't show again" checkbox (provided by the caller
 * via `finalStepContent`); ticking it routes the terminal status to
 * 'dismissed_permanent' via TourProvider.
 */

import type { ReactNode } from 'react';
import type { Step } from 'react-joyride';

export const FOUNDATIONAL_TOUR_ID = 'foundational';
export const FOUNDATIONAL_TOUR_VERSION = 'v2';

/**
 * Build the foundational tour. The final step's `content` is provided by the
 * caller so the consuming component can inject the "Don't show again"
 * checkbox while keeping the step copy in this module.
 */
export function buildFoundationalTour(finalStepContent: ReactNode): Step[] {
  return [
    {
      target: '[data-tour-id="contacts.new-contact-button"]',
      content:
        'Add your first contact here. New contacts default to "Not Yet Contacted" so you always know who you still need to reach.',
      placement: 'bottom',
      // react-joyride v3 renamed `disableBeacon` → `skipBeacon`. Same effect:
      // skip the standalone beacon dot and open the tooltip immediately.
      skipBeacon: true,
    },
    {
      target: '[data-tour-id="contacts.filters-button"]',
      content:
        'Find people fast — filter by status, by upcoming birthday, or by recent call outcome.',
      placement: 'bottom',
      // Spotlight target interaction (clicking through the cutout to the
      // underlying element) is enabled by default in react-joyride v3
      // (`blockTargetInteraction: false`); no per-step opt-in is needed.
    },
    {
      target: '[data-tour-id="help.restart-tour"]',
      content: finalStepContent,
      placement: 'left',
    },
  ];
}
